/**
 * Exponential backoff with jitter.
 *
 * Jitter is not decoration. Every scorer at a ground shares one bad tower, and a stadium's
 * worth of devices that all lost the network at the same moment will all come back at the
 * same moment — so a bare doubling schedule synchronises them into a thundering herd
 * against a backend pinned at one instance (ADR-0012). Spreading each device randomly
 * across its window is what breaks the convoy.
 *
 * `Math.random` is injected rather than called, because a scheduler nobody can test
 * deterministically is a scheduler nobody has tested.
 */

export interface BackoffOptions {
  /** Delay after the first failure, in milliseconds. */
  readonly baseMs: number;
  /** Never wait longer than this, however many failures have piled up. */
  readonly maxMs: number;
  /** Fraction of the window to jitter by, 0–1. */
  readonly jitter: number;
  readonly random: () => number;
}

export const DEFAULT_BACKOFF: Omit<BackoffOptions, "random"> = {
  // A scorer is mid-over. The first retry has to be fast enough that a one-off blip is
  // invisible, which means sub-second.
  baseMs: 500,
  // Capped well under a minute: the pill would otherwise sit on "Syncing" for minutes
  // after the network returned, which reads as broken even though it is working.
  maxMs: 30_000,
  jitter: 0.3,
};

/**
 * How long to wait before attempt `failures + 1`.
 *
 * Full jitter over a *portion* of the window rather than over the whole of it: pure full
 * jitter can pick a near-zero delay after ten failures, which defeats the point of having
 * backed off at all.
 */
export function backoffDelay(failures: number, options: BackoffOptions): number {
  if (failures <= 0) return 0;
  const exponential = Math.min(options.maxMs, options.baseMs * 2 ** (failures - 1));
  const spread = exponential * options.jitter;
  // Centred on the exponential value, so the average schedule is the schedule.
  const offset = (options.random() * 2 - 1) * spread;
  return Math.max(0, Math.round(exponential + offset));
}
